/**
 * Dependencies.
 */
import Moment from 'moment-timezone';
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';

/**
 * Component.
 */
@Component({
  name: 'choose-day-component'
})
export default class extends Vue {
  /**
   * Property `moment`.
   */
  moment = Moment;

  /**
   * Property `maxDays`.
   */
  maxDays = 8;

  /**
   * Property `page`.
   */
  page = 1;

  /**
   * Property `daysPerPage`.
   */
  daysPerPage = 11;

  /**
   * Property `openDate`.
   * To always use the same day in case of a change of day (midnight transition).
   */
  openDate: string = this.moment().toString();

  /**
   * Property `selectedDate`.
   */
  selectedDate: string = null;

  /**
   * Property `expirationDate`.
   */
  @Prop({ type: String })
  expirationDate: string;

  /**
   * Property `expectedOrderDate`.
   */
  @Prop({ type: String })
  expectedOrderDate: string;

  /**
   * Lifecycle created.
   */
  created() {
    if (this.expirationDate) {
      const expiration = this.moment(this.expirationDate).startOf('day');

      if (expiration.isValid()) {
        const now = this.moment(this.openDate).startOf('day');

        const maxDays: number =
          this.moment
            .duration(expiration.subtract(1, 'days').diff(now))
            .asDays() || 0;

        if (maxDays <= this.maxDays) {
          this.maxDays = maxDays >= 0 ? maxDays : 0;
        }
      }
    }
  }

  /**
   * Handle day.
   */
  handleDay({ column, row }: { column: number; row: number }) {
    const days = this.handleCalculateDays({ column, row });

    return this.moment(this.openDate)
      .add(days, 'days')
      .format('ddd');
  }

  /**
   * Handle day and month.
   */
  handleDayAndMonth({ column, row }: { column: number; row: number }) {
    const days = this.handleCalculateDays({ column, row });

    return this.moment(this.openDate)
      .add(days, 'days')
      .format('DD MMM');
  }

  /**
   * Handle calculate day.
   */
  handleCalculateDays({ column, row }: { column: number; row: number }) {
    const perPage = this.page !== 1 ? (this.page - 1) * this.daysPerPage : 0;
    const days = (row - 1) * 4 + column + perPage;

    return days;
  }

  /**
   * Handle has max days reached.
   */
  handleHasMaxDaysReached({ column, row }: { column: number; row: number }) {
    const currentDays = this.handleCalculateDays({ column, row });

    return currentDays > this.maxDays;
  }

  /**
   * Handle more days.
   */
  handleMoredays() {
    this.page++;

    this.daysPerPage = 10;
  }

  /**
   * Handle less days.
   */
  handleLessdays() {
    this.page--;

    if (this.page === 1) {
      this.daysPerPage = 11;
    }
  }

  /**
   * Handle is weekend.
   */
  handleIsWeekend({ column, row }: { column: number; row: number }) {
    const days = this.handleCalculateDays({ column, row });

    return (
      this.moment(this.openDate)
        .add(days, 'days')
        .day() === 0 ||
      this.moment(this.openDate)
        .add(days, 'days')
        .day() === 6
    );
  }

  /**
   * Handle is same as expected order date.
   */
  handleIsSameAsExpectedOrderDate({
    column,
    row
  }: {
    column: number;
    row: number;
  }) {
    let result = false;

    if (this.expectedOrderDate) {
      const days = this.handleCalculateDays({ column, row });

      const day = this.moment(this.openDate)
        .add(days, 'days')
        .startOf('day');

      const expectedOrderDate = this.moment(this.expectedOrderDate).startOf(
        'day'
      );

      result = day.isSame(expectedOrderDate);
    }

    return result;
  }

  /**
   * Handle can select day.
   */
  handleCanSelectDay({ column, row }: { column: number; row: number }) {
    return (
      !this.handleIsWeekend({ column, row }) &&
      !this.handleHasMaxDaysReached({ column, row }) &&
      !this.handleIsSameAsExpectedOrderDate({ column, row })
    );
  }

  /**
   * Handle selected day.
   */
  handleSelectDay({ column, row }: { column: number; row: number }) {
    if (this.handleCanSelectDay({ column, row })) {
      const days = this.handleCalculateDays({ column, row });

      this.selectedDate = this.moment(this.openDate)
        .add(days, 'days')
        .startOf('day')
        .toISOString();

      this.$emit('update-day', { day: this.selectedDate });
    }
  }
}
